import { useEffect, useMemo, useState, useRef } from "react";
import { Search, Plus, Minus, Trash2, Save, RotateCcw, X } from "lucide-react";
import { toast } from "react-toastify";
import { apiClient } from "../../apiclient/apiclient";
import socket from "../../socket/socketClient.js";
import POSCheckoutModal from "./components/POSCheckoutModal";
import BillSummary from "./components/BillSummary";
import { printThermal } from "../../components/ThermalPrint";
import Loader from "../../components/Loader";
import WarehouseSelectModal from "./components/WarehouseSelectModal";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const PosPage = () => {
  // Modal state for warehouse selection
  const [warehouseModal, setWarehouseModal] = useState({ open: false, product: null });
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  const [warehouses, setWarehouses] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [activeCategories, setActiveCategories] = useState(["all"]);
  const [activeBrand, setActiveBrand] = useState("all");
  const [cart, setCart] = useState([]);
  const [isPOSCheckoutModalOpen, setIsPOSCheckoutModalOpen] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [orderDate, setOrderDate] = useState(new Date());
  const [isBackdated, setIsBackdated] = useState(false);

  const [checkoutMode, setCheckoutMode] = useState(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);

  const [splitHeight, setSplitHeight] = useState(50);
  const [isMobile, setIsMobile] = useState(false);
  const mainGridRef = useRef(null);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Socket listener for real-time product updates
  useEffect(() => {
    if (!socket) return;
    
    const handleProductUpdate = async (data) => {
      if (data && data.productId && data.warehouses) {
        // Silently update only that specific product's warehouse stock in state
        setProducts((prevProducts) =>
          prevProducts.map((p) =>
            p._id === data.productId ? { ...p, warehouses: data.warehouses } : p
          )
        );
      } else {
        // Fallback: silently reload everything in the background without showing the loader spinner
        try {
          const res = await apiClient.get("/products?lite=true");
          if (res.data) {
            setProducts(res.data);
          }
        } catch (err) {
          console.error("Silent reload failed", err);
        }
      }
    };
    
    socket.on("product_updated", handleProductUpdate);
    
    return () => {
      socket.off("product_updated", handleProductUpdate);
    };
  }, []);

  // Auto-adjust cart items when products list is loaded or updated
  useEffect(() => {
    if (!products || products.length === 0 || cart.length === 0) return;
    
    setCart((prevCart) => {
      let cartChanged = false;
      const updatedCart = prevCart.map((item) => {
        const matchingProduct = products.find((p) => p._id === item._id);
        if (!matchingProduct) return item; // product not found in new list, keep it
        
        // Find matching warehouse and expiry
        const matchingWarehouse = matchingProduct.warehouses?.find((w) => {
          const matchesLoc = (w.location?._id || w.location || w.locationId)?.toString() === item.selectedWarehouseId;
          const wTime = w.expiryDate ? new Date(w.expiryDate).getTime() : null;
          const iTime = item.expiryDate ? new Date(item.expiryDate).getTime() : null;
          return matchesLoc && wTime === iTime;
        });
        
        const availableStock = matchingWarehouse ? (matchingWarehouse.quantity || 0) : 0;
        
        if (item.qty > availableStock) {
          cartChanged = true;
          // Warn client
          toast.warn(`Cart updated: ${item.name} stock was modified on another device.`);
          return { ...item, qty: availableStock };
        }
        return item;
      }).filter((item) => item.qty > 0); // Remove items if stock went to 0
      
      return cartChanged ? updatedCart : prevCart;
    });
  }, [products]);

  const handleStartDrag = (e) => {
    e.preventDefault();
    const handleMove = (moveEvent) => {
      if (!mainGridRef.current) return;
      const currentY = moveEvent.touches ? moveEvent.touches[0].clientY : moveEvent.clientY;
      const rect = mainGridRef.current.getBoundingClientRect();
      const newHeight = ((currentY - rect.top) / rect.height) * 100;
      setSplitHeight(Math.max(20, Math.min(80, newHeight)));
    };

    const handleEnd = () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleEnd);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleEnd);
    };

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleEnd);
    window.addEventListener("touchmove", handleMove);
    window.addEventListener("touchend", handleEnd);
  };

  useEffect(() => {
    const savedDraft = localStorage.getItem("pos_order_draft");
    if (savedDraft) {
      setShowDraftBanner(true);
    }
  }, []);

  const saveDraft = () => {
    const draftData = {
      selectedClient,
      cart,
      discount,
      isBackdated,
      orderDate,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("pos_order_draft", JSON.stringify(draftData));
    toast.success("POS Draft saved successfully");
    setShowDraftBanner(false);
  };

  const restoreDraft = () => {
    try {
      const savedDraft = localStorage.getItem("pos_order_draft");
      if (!savedDraft) return;

      const data = JSON.parse(savedDraft);
      setSelectedClient(data.selectedClient || null);
      setCart(data.cart || []);
      setDiscount(data.discount || 0);
      setIsBackdated(data.isBackdated || false);
      if (data.orderDate) {
        setOrderDate(new Date(data.orderDate));
      }
      setShowDraftBanner(false);
      toast.success("Draft restored");
    } catch (err) {
      toast.error("Failed to restore draft");
    }
  };

  const clearDraft = () => {
    localStorage.removeItem("pos_order_draft");
    setShowDraftBanner(false);
    toast.info("Draft cleared");
  };
  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-md bg-blue-900 px-5 py-2.5 text-sm font-medium text-white " +
    "hover:bg-amber-500 focus:ring-2 focus:ring-amber-400 transition " +
    "disabled:opacity-50 disabled:cursor-not-allowed"
  const inputClass =
    "w-full border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-1 text-sm bg-white outline-none transition"

  // "cash" | "credit"

  // Calculate final total
  // const finalTotal = useMemo(() => {
  //   // Ensure subtotal and discount are numbers
  //   const st = Number(subtotal) || 0;
  //   const disc = Number(discount) || 0;
  //   return Math.max(0, st - disc);
  // }, [subtotal, discount]);

  const payAndPrintInProgress = useRef(false);
  const payAndPrint = async (paidAmount, { redeemedPoints = 0, pointsDiscount = 0 } = {}) => {
    if (payAndPrintInProgress.current) return;
    payAndPrintInProgress.current = true;

    if (!cart.length) {
      payAndPrintInProgress.current = false;
      return toast.error("Cart is empty");
    }

    try {
        const today = new Date();
      const isSameDate = orderDate &&
        orderDate.getDate() === today.getDate() && 
        orderDate.getMonth() === today.getMonth() && 
        orderDate.getFullYear() === today.getFullYear();
      
      const finalOrderDate = isBackdated && orderDate && !isSameDate ? orderDate.toISOString() : undefined;
      const orderPayload = {
        orderMode: "payNow",
        clientId: selectedClient || null,
        items: cart.map(i => ({
          productId: i._id,
          product: i._id,
          productName: i.name,
          quantity: i.qty,
          price:
            i.manualAmount !== null &&
              i.manualAmount !== "" &&
              !isNaN(i.manualAmount)
              ? Number(i.manualAmount)
              : Number(i.taxInclude === false ? (i.priceWithGst || i.price) : i.price),
          warehouseId: i.selectedWarehouseId,
          warehouseName: i.selectedWarehouseName || '',
          quantityValue: i.quantityValue || '',
          quantityUnit: i.quantityUnit || '',
          taxRate: i.taxRate || 0,
          taxInclude: i.taxInclude ?? true,
          expiryDate: i.expiryDate || null,
          batchId: i.batchId || null,
        })),
        paymentDetails: {
          discount: Number(discount) + Number(pointsDiscount),
          redeemedPoints,
          pointsDiscount: Number(pointsDiscount),
          payments: [
            {
              mode: "Cash",
              amount: Number(paidAmount),
            },
          ],
        },
        notes: "POS Cash Order",
         isBackdated: isBackdated && !isSameDate,
        orderDate: finalOrderDate,
      };

      await apiClient.post("/orders/pos", orderPayload);

      toast.success("Payment successful!");
      localStorage.removeItem("pos_order_draft");


      setCart([]);
      setShowCheckout(false);
      setDiscount(0);
      setSelectedClient(null);
      await reloadProducts();

    } catch (err) {
      toast.error(err.response?.data?.message || "Checkout failed");
    } finally {
      payAndPrintInProgress.current = false;
    }
  };

  const reloadProducts = async () => {
    try {
      setLoadingProducts(true);
      const res = await apiClient.get("/products?lite=true");
      const productsData = res.data || [];
      setProducts(productsData);
    } catch (err) {
      console.error("Failed to reload products", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingProducts(true);
        const res = await apiClient.get("/products?lite=true");
        const productsData = res.data || [];
        setProducts(productsData);

        const uniqueCategories = [
          ...new Set(productsData.map(p => p.category?.name).filter(Boolean))
        ];
        //
        const uniqueBrands = [
          ...new Set(productsData.map(p => p.brand?.name).filter(Boolean))
        ];

        setCategories(uniqueCategories);
        setBrands(uniqueBrands);
      } catch (err) {
        console.error("Failed to load products", err);
      } finally {
        setLoadingProducts(false);
      }

      // load warehouses/locations
      try {
        const locRes = await apiClient.get("/locations");
        const locs = locRes.data?.locations || locRes.data || [];
        setWarehouses(locs);
        // Do not auto-select warehouse to allow global product search
      } catch (err) {
        console.error("Failed to load locations", err);
      }

      // load clients
      try {
        const clientRes = await apiClient.get("/clients?lite=true");
        const clientList = clientRes.data?.clients || clientRes.data || [];
        setClients(clientList);
      } catch (err) {
        console.error("Failed to load clients", err);
      }

      // load loyalty settings
      try {
        const loyaltyRes = await apiClient.get("/loyalty-settings");
        if (loyaltyRes.data) {
          setLoyaltySettings(loyaltyRes.data);
        }
      } catch (err) {
        console.error("Failed to load loyalty settings", err);
      }
    };

    fetchData();
  }, []);


  const handleClientAdded = (client, isEdit) => {
    if (isEdit) {
      setClients((prev) =>
        prev.map((c) => (c._id === client._id ? client : c))
      );
    } else {
      setClients((prev) => [...prev, client]);
    }

    // ✅ AUTO SELECT NEW CLIENT
    setSelectedClient(client._id);
  };

  /* ================= FILTER ================= */
  const filteredProducts = useMemo(() => {
    // Only include products that have a warehouse entry for the selected warehouse
    let filtered = products.filter(p => {
      // Category filter (multi-select)
      const categoryMatch =
        activeCategories.includes("all") ||
        (p.category?.name && activeCategories.includes(p.category.name));

      // Brand filter
      const brandMatch =
        activeBrand === "all" ||
        p.brand?.name === activeBrand;

      // Ignore selected warehouse filter
      const warehouseStock = true;

      // Search filter overrides category and brand filters but applies local text match to prevent UI flash
      if (searchTerm.trim()) {
        const term = searchTerm.trim().toLowerCase();
        const matchesSearch = 
          p.name?.toLowerCase().includes(term) ||
          p.brand?.name?.toLowerCase().includes(term) ||
          p.category?.name?.toLowerCase().includes(term);
        
        return matchesSearch && warehouseStock;
      }

      // Only include if the product exists in the selected warehouse
      return categoryMatch && brandMatch && warehouseStock;
    });

    // Sort: in-stock first, out-of-stock last
    return filtered.sort((a, b) => {
      const aStock = a.totalQuantity || 0;
      const bStock = b.totalQuantity || 0;
      // In-stock first
      if (aStock > 0 && bStock === 0) return -1;
      if (aStock === 0 && bStock > 0) return 1;
      return 0;
    });
  }, [products, activeCategories, activeBrand, selectedWarehouse, searchTerm]);




  // ================= CART =================
  const subtotal = useMemo(
    () => cart.reduce((sum, i) => {
      const defaultPrice = i.taxInclude === false ? (i.priceWithGst || i.price) : i.price;
      const itemPrice = i.manualAmount !== null && !isNaN(i.manualAmount) ? i.manualAmount : defaultPrice;
      return sum + itemPrice * i.qty;
    }, 0),
    [cart]
  );

  const finalTotal = useMemo(() => {
    // Ensure subtotal and discount are numbers
    const st = Number(subtotal) || 0;
    const disc = Number(discount) || 0;
    return Number(Math.max(0, st - disc).toFixed(2));
  }, [subtotal, discount]);

  const addToCart = (product) => {
    // Deprecated: now handled in ProductCard/modal
    // No-op
  };

  const changeQty = (id, selectedWarehouseId, expiryDate, diff) => {
    setCart(prev => prev.map(i => {
      const isMatch = i._id === id && 
                      i.selectedWarehouseId === selectedWarehouseId && 
                      (i.expiryDate === expiryDate || (!i.expiryDate && !expiryDate));
      if (isMatch) {
        // Find the product and its stock in the selected warehouse for this cart item
        const product = products.find(p => p._id === id);
        let warehouseStock = null;
        if (product && i.selectedWarehouseId) {
          warehouseStock = product.warehouses?.find(w => {
            const matchesLoc = (w.location?._id || w.location || w.locationId)?.toString() === i.selectedWarehouseId;
            const wTime = w.expiryDate ? new Date(w.expiryDate).getTime() : null;
            const iTime = i.expiryDate ? new Date(i.expiryDate).getTime() : null;
            return matchesLoc && wTime === iTime;
          });
        }
        const availableStock = warehouseStock?.quantity || 0;
        // Only allow increase if it doesn't exceed stock
        if (diff > 0 && i.qty + diff > availableStock) {
          toast.warn("Cannot add more than available stock");
          return i;
        }
        return { ...i, qty: i.qty + diff };
      }
      return i;
    }).filter(i => i.qty > 0));
  };

  const handleCreditOrder = async (payments, { redeemedPoints = 0, pointsDiscount = 0 } = {}) => {
    if (payAndPrintInProgress.current) return;
    payAndPrintInProgress.current = true;
    try {
        const today = new Date();
      const isSameDate = orderDate &&
        orderDate.getDate() === today.getDate() && 
        orderDate.getMonth() === today.getMonth() && 
        orderDate.getFullYear() === today.getFullYear();
      
      const finalOrderDate = isBackdated && orderDate && !isSameDate ? orderDate.toISOString() : undefined;

      const manualPaid = payments.reduce(
        (sum, p) => sum + Number(p.amount || 0),
        0
      );

      const orderPayload = {
        orderMode: "payLater",
        clientId: selectedClient,
        items: cart.map(i => ({
          productId: i._id,
          productName: i.name,
          product: i._id,
          productName: i.name,
          quantity: i.qty,
          price:
            i.manualAmount !== null &&
              i.manualAmount !== "" &&
              !isNaN(i.manualAmount)
              ? Number(i.manualAmount)
              : Number(i.taxInclude === false ? (i.priceWithGst || i.price) : i.price),
          warehouseId: i.selectedWarehouseId,
          warehouseName: i.selectedWarehouseName || '',
          quantityValue: i.quantityValue || '',
          quantityUnit: i.quantityUnit || '',
          taxRate: i.taxRate || 0,
          taxInclude: i.taxInclude ?? true,
          expiryDate: i.expiryDate || null,
          batchId: i.batchId || null,
        })),
        paymentDetails: {
          discount: Number(discount) + Number(pointsDiscount),
          redeemedPoints,
          pointsDiscount: Number(pointsDiscount),
          payments: payments.map(p => ({
            mode: p.mode,
            amount: Number(p.amount || 0),
          })),
        },
        notes: "POS Credit Order",
          isBackdated: isBackdated && !isSameDate,
        orderDate: finalOrderDate,
      };


      const res = await apiClient.post("/orders/pos", orderPayload);
      
      toast.success("Credit order saved successfully.");
      localStorage.removeItem("pos_order_draft")
      // 🖨 Thermal Print
      // if (res.data && res.data.order) {
      //   const { printThermal } = await import("../../components/ThermalPrint");
      //   printThermal(res.data.order);
      // }
      setCart([]);
      setShowCheckout(false);
      setDiscount(0);
      setSelectedClient(null);
      // Refresh products after credit order
      await reloadProducts();

    } catch (err) {
      toast.error(err.response?.data?.message || "Credit save failed");
    } finally {
      payAndPrintInProgress.current = false;
    }
  };

  const handleManualAmountChange = (id, selectedWarehouseId, expiryDate, value) => {
    setCart(prev =>
      prev.map(i => {
        const isMatch = i._id === id &&
          i.selectedWarehouseId === selectedWarehouseId &&
          (i.expiryDate === expiryDate || (!i.expiryDate && !expiryDate));
        return isMatch
          ? { ...i, manualAmount: value } 
          : i;
      })
    );
  };

  const handleFinalCheckout = async () => {
    if (payAndPrintInProgress.current) return;
    payAndPrintInProgress.current = true;

    if (!cart.length) {
      payAndPrintInProgress.current = false;
      return toast.error("Cart is empty");
    }

    const change = Math.max(0, receivedAmount - finalTotal);

    if (receivedAmount < finalTotal) {
      return toast.error("Insufficient payment");
    }

    try {
      const order = {
        items: cart.map(i => ({
          productId: i._id,
          quantity: i.qty,
          price: i.manualAmount !== null && i.manualAmount !== "" && !isNaN(i.manualAmount)
            ? Number(i.manualAmount)
            : Number(i.taxInclude === false ? (i.priceWithGst || i.price) : i.price),
          warehouseId: i.selectedWarehouseId,
          expiryDate: i.expiryDate || null,
          batchId: i.batchId || null,
        })),
        subtotal,
        discount,
        totalAmount: finalTotal,
        receivedAmount,
        change,
        paymentStatus: "paid",
      };

      await apiClient.post("/orders/pos", order);
      toast.success("Order created successfully");
      setCart([]);
      setShowCheckout(false);
      setReceivedAmount(0);
      setDiscount(0);
    } catch (err) {
      toast.error(err.response?.data?.message || "Checkout failed");
    } finally {
      payAndPrintInProgress.current = false;
    }
  };

  return (
    <div className="h-full flex flex-col relative">
  
      {/* DRAFT RESTORATION BANNER */}
      {showDraftBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center justify-between animate-in fade-in slide-in-from-top-4 duration-500 shrink-0">
          <div className="flex items-center gap-2">
            <span className="flex h-2 w-2 rounded-full bg-amber-500 animate-pulse"></span>
            <p className="text-xs font-medium text-amber-800">
              You have an unsaved POS draft from{" "}
              {new Date(
                JSON.parse(localStorage.getItem("pos_order_draft")).timestamp,
              ).toLocaleString()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={restoreDraft}
              className="text-[10px] font-bold uppercase tracking-wider text-amber-700 hover:text-amber-900 flex items-center gap-1"
            >
              <RotateCcw size={12} /> Restore
            </button>
            <button
              onClick={clearDraft}
              className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-slate-700 flex items-center gap-1"
            >
              <X size={12} /> Discard
            </button>
          </div>
        </div>
      )}
      {/* ================= MAIN GRID ================= */}
      <div ref={mainGridRef} className="flex flex-col md:flex-row flex-1 overflow-hidden gap-0">

        {/* ================= PRODUCTS ================= */}
        <div 
          style={isMobile ? { height: `${splitHeight}%` } : {}} 
          className={`w-full md:w-2/3 ${isMobile ? "" : "h-full md:border-r"} bg-white overflow-y-auto border-b md:border-b-0`}
        >
          {/* Search Bar & Categories */}
          <div className="mb-0 bg-blue-50 px-3 py-3 border-b flex items-center gap-3">
            {/* Scrollable Categories */}
            <div className="flex-1 overflow-x-auto flex items-center gap-2 pb-1" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style>{`
                  .flex-1::-webkit-scrollbar {
                    display: none;
                  }
                `}</style>
              <button
                className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition ${activeCategories.includes("all") ? "bg-orange-500 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 shadow-sm"}`}
                onClick={() => setActiveCategories(["all"])}
              >
                All
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  className={`whitespace-nowrap px-4 py-1.5 rounded-full text-sm font-medium transition ${activeCategories.includes(c) ? "bg-orange-500 text-white shadow-sm" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 shadow-sm"}`}
                  onClick={() => {
                    if (activeCategories.includes("all")) {
                      setActiveCategories([c]);
                    } else if (activeCategories.includes(c)) {
                      const next = activeCategories.filter(cat => cat !== c);
                      setActiveCategories(next.length === 0 ? ["all"] : next);
                    } else {
                      setActiveCategories([...activeCategories, c]);
                    }
                  }}
                >
                  {c}
                </button>
              ))}
            </div>

            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-40 sm:w-56 border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded-lg px-3 py-2 text-sm bg-white outline-none transition shrink-0"
              autoComplete="off"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-0 min-h-[120px]">
            {loadingProducts ? (
              <div className="col-span-full flex justify-center items-center h-32">
                <Loader size={32} />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="col-span-full text-center text-gray-400">No products found</div>
            ) : (
              filteredProducts.map((product) => (
                <ProductCard
                  key={product._id}
                  product={product}
                  cart={cart}
                  setCart={setCart}
                  onWarehouseSelect={prod => setWarehouseModal({ open: true, product: prod })}
                />
              ))
            )}
          </div>
        </div>

        {/* Warehouse Select Modal */}
        {warehouseModal.open && warehouseModal.product && (
          <WarehouseSelectModal
            warehouses={warehouseModal.product.warehouses
              .filter(w => (w.quantity || 0) > 0)
              .map(w => ({
                ...w,
                name: w.location?.name || w.locationName || 'Warehouse',
                locationId: (w.location?._id || w.location || w.locationId)?.toString(),
              }))}
            item={warehouseModal.product}
            cart={cart}
            setCart={setCart}
            onClose={() => setWarehouseModal({ open: false, product: null })}
          />
        )}

        {isMobile && (
          <div
            onMouseDown={handleStartDrag}
            onTouchStart={handleStartDrag}
            className="h-3 bg-slate-100 hover:bg-blue-100 active:bg-blue-200 cursor-row-resize flex items-center justify-center border-y border-slate-200 select-none shrink-0"
          >
            <div className="w-12 h-1 bg-slate-400 rounded-full"></div>
          </div>
        )}

        {/* ================= CART ================= */}
        <div 
          style={isMobile ? { height: `${100 - splitHeight}%` } : {}} 
          className={`w-full md:w-1/3 ${isMobile ? "" : "h-full md:border-l"} bg-white border-t md:border-t-0 flex flex-col min-h-0`}
        >


          {/* Backdate Toggle */}
            <div className="px-4 pt-2 pb-2 border-b flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="isBackdatedPos"
                  checked={isBackdated}
                  onChange={(e) => {
                    setIsBackdated(e.target.checked);
                    if (!e.target.checked) setOrderDate(new Date());
                  }}
                  className="w-3.5 h-3.5 accent-blue-600 cursor-pointer"
                />
                <label htmlFor="isBackdatedPos" className="font-semibold text-[11px] uppercase tracking-wider cursor-pointer text-slate-600">Backdate Order</label>
              </div>
              
              {isBackdated && (
                <DatePicker
                  selected={orderDate}
                  onChange={(date) => setOrderDate(date)}
                  dateFormat="dd/MM/yyyy"
                  maxDate={new Date()}
                  popperPlacement="bottom-end"
                  className="border border-slate-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 rounded px-2 py-1 text-xs bg-white outline-none transition w-24 text-center text-slate-700"
                />
              )}
            </div>
          {/* Scrollable Cart */}
          <div className="flex-1 min-h-0 overflow-y-auto pt-3">
            {/* <h3 className="font-semibold mb-3">Cart</h3> */}

            {cart.length === 0 && (
              <p className="text-sm text-gray-400 ms-4">Cart is empty</p>
            )}

            {cart.map((i, idx) => (
              <div key={`${i._id}_${i.selectedWarehouseId || ""}_${i.expiryDate || ""}`} className="flex justify-between items-center mb-3 pb-3 px-2 border-b">
                <div>
                  <div className="text-sm font-medium">
                    {i.name} {i.quantityValue && i.quantityUnit && (
                      <span className="ml-1 text-slate-500">({i.quantityValue}{i.quantityUnit})</span>
                    )}
                  </div>
                  <div className="flex flex-col text-xs text-slate-400 mt-1">
                    <div className="flex items-center gap-1.5">
                      {i.selectedWarehouseName && (
                        <span className="font-semibold text-blue-700 bg-blue-50 px-1 py-0.2 rounded border border-blue-100">
                          {i.selectedWarehouseName}
                        </span>
                      )}
                      {i.expiryDate && (
                        <span className="font-semibold text-amber-700 bg-amber-50 px-1 py-0.2 rounded border border-amber-100">
                          Exp: {new Date(i.expiryDate).toLocaleDateString()}
                        </span>
                      )}
                      {i.batchId && i.batchId !== "N/A" && (
                        <span className="font-mono text-[10px] text-slate-500 bg-slate-100 px-1 rounded border">
                          {i.batchId}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500 mt-1.5">
                    <span>Qty: {i.qty}</span>
                    <span>|</span>
                    <input
                      type="text"
                      placeholder="unit price"
                      value={i.manualAmount !== null && i.manualAmount !== undefined ? i.manualAmount : Number(i.taxInclude === false ? (i.priceWithGst || i.price) : i.price).toFixed(2)}
                      onChange={e => handleManualAmountChange(i._id, i.selectedWarehouseId, i.expiryDate, e.target.value)}
                      className={inputClass}
                      style={{ width: 90 }}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Minus size={14} className="cursor-pointer" onClick={() => changeQty(i._id, i.selectedWarehouseId, i.expiryDate, -1)} />
                  <span>{i.qty}</span>
                  <Plus size={14} className="cursor-pointer" onClick={() => changeQty(i._id, i.selectedWarehouseId, i.expiryDate, 1)} />
                  <Trash2 size={14} className="cursor-pointer" onClick={() => changeQty(i._id, i.selectedWarehouseId, i.expiryDate, -i.qty)} />
                </div>
              </div>
            ))}
          </div>

          {/* Sticky Checkout Button */}
          <div className="sticky bottom-0 left-0 right-0 bg-white z-10 flex mx-2 gap-2 pb-3 pt-2 border-t">
            <button
              className="flex-1 bg-orange-500 hover:bg-amber-500 text-white py-2 px-2 rounded-md font-medium text-sm transition"
              onClick={() => {
                if (cart.length === 0) {
                  toast.error("Cart is empty");
                  return;
                }
                setCheckoutMode("cash");
                setIsPOSCheckoutModalOpen(true);
              }}
            >
              Pay Now ₹{finalTotal.toFixed(2)}
            </button>

            <button
              className="flex-1 border-2 border-[#172554] bg-[#172554] text-white py-2 px-2 rounded-md font-medium text-sm transition-all duration-200"
              onClick={() => {
                if (cart.length === 0) {
                  toast.error("Cart is empty");
                  return;
                }
                setCheckoutMode("credit");
                setIsPOSCheckoutModalOpen(true);
              }}
            >
              Pay Later
            </button>
          </div>
 <div className="px-3 pb-3 bg-slate-50 flex gap-2">
            <button
              className="flex-1 bg-[#172554] text-white hover:bg-[#1e346e] font-semibold py-2 rounded-md transition-colors text-sm flex items-center justify-center gap-2"
              onClick={saveDraft}
            >
              <Save size={16} /> Save Draft
            </button>
          </div>

        </div>
      </div>

      {/* ================= CHECKOUT CUSTOMER LOOKUP MODAL ================= */}
      <POSCheckoutModal
        isOpen={isPOSCheckoutModalOpen}
        onClose={() => setIsPOSCheckoutModalOpen(false)}
        checkoutMode={checkoutMode}
        clients={clients}
        onAddClient={handleClientAdded}
        onCheckout={(clientId) => {
          setSelectedClient(clientId);
          setShowCheckout(true);
        }}
      />

      {/* ================= BILL SUMMARY DRAWER ================= */}
      {showCheckout && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setShowCheckout(false)}
          />

          {/* Drawer */}
          <div
            className="fixed right-0 top-[64px] h-[calc(100vh-64px)] w-full lg:w-[400px] bg-white shadow-2xl z-50 flex flex-col animate-slide-in"
          >
            <BillSummary
              mode={checkoutMode}
              cart={cart}
              subtotal={subtotal}
              discount={discount}
              setDiscount={setDiscount}
              finalTotal={finalTotal}
              selectedClientData={clients.find(
                (c) => c._id === selectedClient
              )}
              loyaltySettings={loyaltySettings}
               orderDate={orderDate}
              onPayAndPrint={async (paidAmount, options) => {
                setReceivedAmount(paidAmount);
                await payAndPrint(paidAmount, options);
              }}
              onPayLater={handleCreditOrder}
              onClose={() => setShowCheckout(false)}
            />

          </div>
        </>
      )}


    </div>
  );





};

/* ================= SIDEBAR ITEM ================= */
const SidebarItem = ({ label, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left px-3 py-2 rounded-lg text-sm mb-1
      ${active
        ? "bg-blue-900 text-white"
        : "hover:bg-gray-100 text-gray-700"
      }`}
  >
    {label}
  </button>
);
const FilterItem = ({ label, active, onClick }) => (
  <label
    className={`flex items-center gap-2 px-3 py-1 text-xs rounded-full cursor-pointer transition ${active
      ? "bg-[#172554] text-white"
      : "bg-gray-100 hover:bg-gray-200"
      }`}
  >
    <input
      type="checkbox"
      checked={active}
      onChange={onClick}
      className="accent-blue-600"
    />
    <span>{label}</span>
  </label>
);
/* ================= PRODUCT CARD ================= */
const ProductCard = ({ product, cart, setCart, onWarehouseSelect }) => {
  // Compute total stock across all warehouses
  const totalStock = product.warehouses?.reduce((sum, w) => sum + (w.quantity || 0), 0) || 0;
  const cartQty = cart.reduce((sum, item) => item._id === product._id ? sum + item.qty : sum, 0);
  const stock = totalStock - cartQty;

  // Handler for product click
  const handleProductClick = () => {
    if (!setCart) return;
    const availableWarehouses = product.warehouses?.filter(w => (w.quantity || 0) > 0) || [];
    if (availableWarehouses.length === 0) {
      toast.warn("No stock available for this product");
      return;
    }
    if (availableWarehouses.length === 1) {
      // Only one warehouse entry, add directly
      const warehouse = availableWarehouses[0];
      const availableStock = warehouse.quantity || 0;
      const warehouseLocationId = (warehouse.location?._id || warehouse.location || warehouse.locationId)?.toString();
      const existing = cart.find(i => 
        i._id === product._id && 
        i.selectedWarehouseId === warehouseLocationId &&
        (i.expiryDate === warehouse.expiryDate || (!i.expiryDate && !warehouse.expiryDate))
      );
      if (existing) {
        if (existing.qty < availableStock) {
          setCart(prev => prev.map(i =>
            i._id === product._id && 
            i.selectedWarehouseId === warehouseLocationId &&
            (i.expiryDate === warehouse.expiryDate || (!i.expiryDate && !warehouse.expiryDate))
              ? { ...i, qty: i.qty + 1 }
              : i
          ));
        } else {
          toast.warn("Cannot add more than available stock");
        }
      } else if (availableStock > 0) {
        setCart(prev => [...prev, { 
          ...product, 
          qty: 1, 
          manualAmount: null, 
          selectedWarehouseId: warehouseLocationId, 
          selectedWarehouseName: warehouse.location?.name || warehouse.locationName || "Warehouse",
          expiryDate: warehouse.expiryDate || null,
          batchId: warehouse.batchId || null
        }]);
      } else {
        toast.warn("No stock available");
      }
    } else {
      // Multiple warehouse entries, call prop to open modal
      if (typeof window !== 'undefined' && typeof onWarehouseSelect === 'function') {
        onWarehouseSelect(product);
      }
    }
  };

  return (
    <div
      onClick={stock > 0 ? handleProductClick : undefined}
      className={`relative border-b border-e p-2 cursor-pointer transition text-xs flex flex-col ${stock > 0 ? "hover:shadow-md" : "opacity-50 cursor-not-allowed"}`}
    >
      {/* CART QUANTITY BADGE */}
      {cartQty > 0 && (
        <div className="absolute top-1 right-1 bg-orange-500 text-white rounded-full w-[22px] h-[22px] flex items-center justify-center font-black text-xs shadow">
          {cartQty}
        </div>
      )}

      {/* IMAGE */}
      <div className="h-16 flex items-center justify-center mb-1">
        {product.thumbnail ? (
          <img
            src={`${process.env.REACT_APP_IMAGE_URL}/${product.thumbnail}`}
            alt={product.name}
            className="h-14 object-contain"
          />
        ) : (
          <div className="h-14 w-14 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-xs">
            {product.name.slice(0, 2).toUpperCase()}
          </div>
        )}
      </div>


      {/* FULL NAME + BRAND + QUANTITY */}
      <div className="font-semibold text-center break-words">
        <span>{product.name}</span>

        {/* {product.quantityValue && product.quantityUnit && (
          <span className="block text-xs text-gray-500 mt-1">{`${product.quantityValue}${product.quantityUnit}`}</span>
        )} */}
        {product.brand?.name && (
          <span className="block text-xs text-blue-700 mt-0">{product.brand.name}</span>
        )}
      </div>

      {/* PRICE */}
      <div className="text-center font-bold text-blue-900 mt-0">
        ₹{product.price}
      </div>

      {/* STOCK */}
      <div className="text-center mt-0">
        {stock > 0 ? (
          <span className="text-green-600 text-xs font-medium">{stock} left</span>
        ) : (
          <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full text-xs font-medium">Out of Stock</span>
        )}
      </div>
    </div>
  );
};



export default PosPage;
